import { Sensitivity } from './choices/sensitivity';
import { SymptomType } from './choices/symptom-type';
import { Anamnesis, Finding, Symptom, SymptomLike } from './symptom';

export interface CauseLikeConstructorOpts {
  sensitivity?: Sensitivity;
  symptom: SymptomLike;
}

/**
 * Class for all Diagnosis–SymptomLike relationships.
 */
export class Cause {
  private readonly _sensitivity?: Sensitivity;
  public readonly symptom: SymptomLike;

  constructor(opts: CauseLikeConstructorOpts) {
    this._sensitivity = opts.sensitivity;
    this.symptom = opts.symptom;
  }

  get sensitivity(): Sensitivity {
    if (!this._sensitivity) {
      throw new Error('No sensitivity set for ' + this.symptom.name);
    }
    return this._sensitivity;
  }

  get hasSensitivity(): boolean {
    return !!this._sensitivity;
  }

  get id(): number {
    // Causes are always linked to an existing SymptomLike
    return this.symptom.id as number;
  }

  get name(): string {
    return this.symptom.name;
  }

  get type(): SymptomType {
    return this.symptom.type;
  }

  isLinked(): boolean {
    return this.symptom.isLinked();
  }

  isSymptom(): boolean {
    return this.symptom.isSymptom();
  }

  isFinding(): boolean {
    return this.symptom.isFinding();
  }

  isAnamnesis(): boolean {
    return this.symptom.isAnamnesis();
  }

  asType(symptomType: SymptomType): SymptomLike {
    switch (symptomType) {
      case SymptomType.SYMPTOM:
        return this.asSymptom();
      case SymptomType.FINDING:
        return this.asFinding();
      case SymptomType.ANAMNESIS:
        return this.asAnamnesis();
    }
    throw new Error('Unknown symptom type: ' + symptomType);
  }

  asSymptom(): Symptom {
    if (!this.isSymptom()) {
      throw new Error('Not a symptom, but a ' + this.symptom.type.label);
    }
    return this.symptom;
  }

  asFinding(): Finding {
    if (!this.isFinding()) {
      throw new Error('Not a finding, but a ' + this.symptom.type.label);
    }
    return this.symptom;
  }

  asAnamnesis(): Anamnesis {
    if (!this.isAnamnesis()) {
      throw new Error('Not an anamnesis, but a ' + this.symptom.type.label);
    }
    return this.symptom;
  }

  toJson(): any {
    return {
      sensitivity: this.sensitivity.id,
      symptom: this.symptom.toJson()
    };
  }

  static fromJson(json: any): Cause {
    if ('sensitivity' in json) {
      return new Cause({
        sensitivity: Sensitivity.fromId(json.sensitivity),
        symptom: SymptomLike.fromJson(json.symptom)
      });
    } else {
      return new Cause({
        symptom: SymptomLike.fromJson(json)
      });
    }
  }

  toString() {
    return (
      '<Cause: ' + this.symptom.name + ', sensitivity=' + this.sensitivity.label + '>'
    );
  }
}
